import { useState, useContext, useEffect } from 'react';
import { IComboBoxOption, MessageBar, MessageBarType, Pivot, PivotItem, ITag, Stack } from "@fluentui/react";
import { FilePicker } from "../../components/FilePicker/FilePicker";
import { FileStatus } from "../../components/FileStatus/FileStatus";
import { TagPickerInline } from "../../components/TagPicker/TagPicker"
import { FolderPicker } from '../../components/FolderPicker/FolderPicker';
import { Tooltips } from '../../components/Tooltips/Tooltips'
import { UserContext } from "../../components/UserContext";
import { PermissionsGroup, PermissionsUser, UserData, getAllFolders, getAllTags } from "../../api";

import styles from "./Content.module.css";
import { getToken } from '../../authConfig';
import { handleError } from '../../toaster';
import { useMsal } from '@azure/msal-react';
import { GroupPicker } from '../../components/GroupPicker';
import { UserPicker } from '../../components/UserPicker';

const Content = () => {
    const client = useMsal().instance;
    const userContext = useContext(UserContext);
    const userData = userContext.userData as UserData;
    const [selectableFolders, setSelectableFolders] = useState<IComboBoxOption[]>([]);
    const [selectableTags, setSelectableTags] = useState<IComboBoxOption[]>([]);
    const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
    const [selectedTags, setSelectedTags] = useState<string[] | undefined>(undefined);
    const [selectedGroups, setSelectedGroups] = useState<PermissionsGroup[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<PermissionsUser[]>([]);

    const onSelectedKeyChanged = (selectedFolders: string[]) => {
        setSelectedKey(selectedFolders[0]);
    };

    const onSelectedTagsChanged = (selectedTags: ITag[]) => {
        setSelectedTags(selectedTags.map((tag) => tag.name));
    }

    const handleLinkClick = (item?: PivotItem) => {
        setSelectedKey(undefined);
    };

    async function fetchFoldersFromCosmos() {
        try {
            const token = client ? await getToken(client) : undefined;
            const response = await getAllFolders(token);
            const folders = response.folders;

            const folderOptions: IComboBoxOption[] = [];
            folders.forEach(folderName => {
                const trimmedFolderName = folderName.trim();
                const textValue = trimmedFolderName === userData.userPrincipalName ? "My Data" : trimmedFolderName;
                folderOptions.push({ key: trimmedFolderName, text: textValue });
            });

            // Check if the user folder exists in folderOptions
            // If it doesn't exist, add it to folderOptions
            const userFolderExists = folderOptions.some(option => option.key === userData.userPrincipalName);
            if (!userFolderExists) {
                folderOptions.push({ key: userData.userPrincipalName, text: "My Data" });
            }

            // Sort folderOptions
            folderOptions.sort((a, b) => a.text.localeCompare(b.text));

            setSelectableFolders(folderOptions);
            setSelectedKey(userData.userPrincipalName) // Default selected folder to user folder
        } catch (error) {
            handleError(error, "Error while fetching folders from API")
        }
    };

    async function fetchTagsFromCosmos() {
        try {
            const token = client ? await getToken(client) : undefined;
            const response = await getAllTags(token);
            const tags = response.tags;

            const tagOptions: IComboBoxOption[] = [];
            tags.forEach(tag => {
                const trimmedTag = tag.trim();
                tagOptions.push({ key: trimmedTag, text: trimmedTag });
            });
            setSelectableTags(tagOptions);
        }
        catch (error) {
            handleError(error, "An unexpected error occurred while fetching tags")
        }
    };

    useEffect(() => {
        async function init() {
            await fetchFoldersFromCosmos();
            await fetchTagsFromCosmos();
        }
        init();
    }, []);

    return (
        <div className={styles.contentArea} >
            <Pivot aria-label="Upload Files Section" className={styles.topPivot} onLinkClick={handleLinkClick}>
                <PivotItem headerText="Upload Files" aria-label="Upload Files Tab">
                    <div className={styles.App} >
                        <Stack>
                            {userData.is_admin ? (
                                <>
                                    <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { width: '100%' } }}>
                                        <Stack.Item grow styles={{ root: { width: '50%' } }}>
                                            <FolderPicker
                                                allowFolderCreation={true}
                                                onSelectedKeyChanged={onSelectedKeyChanged}
                                                selectableFolders={selectableFolders}
                                                selectedKeys={[selectedKey || userData.userPrincipalName]}
                                                userData={userData}
                                            />
                                        </Stack.Item>
                                        <Stack.Item grow styles={{ root: { width: '50%' } }}>
                                            <GroupPicker
                                                selectedGroups={selectedGroups}
                                                onGroupsChange={setSelectedGroups}
                                                isAdmin={userData.is_admin}
                                                useDarkMode={userContext.useDarkMode}
                                            />
                                        </Stack.Item>
                                    </Stack>
                                    <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { width: '100%' } }}>
                                        <Stack.Item grow styles={{ root: { width: '50%' } }}>
                                            <TagPickerInline
                                                allowNewTags={true}
                                                onSelectedTagsChange={onSelectedTagsChanged}
                                                useDarkMode={userContext.useDarkMode}
                                            />
                                        </Stack.Item>
                                        <Stack.Item grow styles={{ root: { width: '50%' } }}>
                                            <UserPicker
                                                selectedUsers={selectedUsers}
                                                onUsersChange={setSelectedUsers}
                                                useDarkMode={userContext.useDarkMode}
                                            />
                                        </Stack.Item>
                                    </Stack>
                                </>
                            ) : (
                                <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { width: '100%' } }}>
                                    <Stack.Item grow styles={{ root: { width: '100%' } }}>
                                        <TagPickerInline
                                            allowNewTags={true}
                                            onSelectedTagsChange={onSelectedTagsChanged}
                                            useDarkMode={userContext.useDarkMode}
                                        />
                                    </Stack.Item>
                                </Stack>
                            )}
                        </Stack>
                        <MessageBar
                            className={styles.messageBarContainer}
                            messageBarType={MessageBarType.info}
                            isMultiline={true}>
                            When uploading a document, it may take a few minutes or more to process depending
                            on document type and size. You can check the status under 'Upload Status'. Once a document has been
                            fully processed and is available to interact with, the State will show as Complete.
                        </MessageBar>
                        <FilePicker
                            folderPath={selectedKey || userData.userPrincipalName}
                            tags={selectedTags || []}
                            groupPermissions={selectedGroups}
                            userPermissions={selectedUsers}
                            useDarkMode={userContext.useDarkMode}
                        />
                    </div>
                </PivotItem>
                <PivotItem headerText="Upload Status" aria-label="Upload Status Tab">
                    <FileStatus
                        selectableFolders={selectableFolders}
                        selectableTags={selectableTags}
                        userData={userData}
                        useDarkMode={userContext.useDarkMode}
                    />
                </PivotItem>
            </Pivot>
            <Tooltips />
        </div >
    );
};

export default Content;